import { baseApi } from "./baseApi";

export interface NewsletterSubscriber {
    id: string;
    email: string;
    name: string;
    mailerLiteId: string | null;
    status: string;
    source: string;
    subscribedAt: string;
    unsubscribedAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface NewsletterStats {
    total: number;
    active: number;
    unsubscribed: number;
}

export interface PaginationMeta {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
}

export interface NewsletterFilters {
    page?: number;
    limit?: number;
    search?: string;
    status?: string;
}

export interface PaginatedSubscribersResponse {
    success: boolean;
    data: NewsletterSubscriber[];
    pagination: PaginationMeta;
    message?: string;
}

export interface StatsResponse {
    success: boolean;
    data: NewsletterStats;
    message?: string;
}

export interface SubscribeData {
    email: string;
    name: string;
}

export interface SubscribeResponse {
    success: boolean;
    message: string;
    data: {
        id: string;
        email: string;
        name: string;
    };
}

/**
 * Newsletter API slice
 * Handles newsletter subscription API calls
 */
export const newsletterApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        // Get all newsletter subscribers (admin only)
        getNewsletterSubscribers: builder.query<
            PaginatedSubscribersResponse,
            NewsletterFilters | void
        >({
            query: (params) => {
                const searchParams = new URLSearchParams();
                if (params?.page) searchParams.append("page", String(params.page));
                if (params?.limit) searchParams.append("limit", String(params.limit));
                if (params?.search) searchParams.append("search", params.search);
                if (params?.status) searchParams.append("status", params.status);
                const queryString = searchParams.toString();
                return {
                    url: `/newsletter/subscribers${queryString ? `?${queryString}` : ""}`,
                    credentials: "include",
                };
            },
            providesTags: ["Newsletter"],
        }),

        // Get newsletter stats (admin only)
        getNewsletterStats: builder.query<StatsResponse, void>({
            query: () => ({
                url: "/newsletter/stats",
                credentials: "include",
            }),
            providesTags: ["Newsletter"],
        }),

        // Subscribe to newsletter (public)
        subscribeToNewsletter: builder.mutation<SubscribeResponse, SubscribeData>({
            query: (data) => ({
                url: "/newsletter/subscribe",
                method: "POST",
                body: data,
            }),
            invalidatesTags: ["Newsletter"],
        }),

        // Unsubscribe from newsletter
        unsubscribeFromNewsletter: builder.mutation<
            { success: boolean; message: string },
            string
        >({
            query: (email) => ({
                url: `/newsletter/unsubscribe/${encodeURIComponent(email)}`,
                method: "DELETE",
            }),
            invalidatesTags: ["Newsletter"],
        }),
    }),
});

export const {
    useGetNewsletterSubscribersQuery,
    useGetNewsletterStatsQuery,
    useSubscribeToNewsletterMutation,
    useUnsubscribeFromNewsletterMutation,
} = newsletterApi;
